import React, { useRef, useState, useEffect, forwardRef } from 'react';

/*------------- IMPORTS FROM MATERIAL UI ------------------------------------*/
import Form from 'rsuite/Form';
import Button from 'rsuite/Button';
import Grid from 'rsuite/Grid';
import Row from 'rsuite/Row';
import Col from 'rsuite/Col';
import { SelectPicker } from 'rsuite';
import { Notification, useToaster } from 'rsuite';

/*------------- OTHERS ------------------------------------------------------*/
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { SchemaModel, StringType } from 'schema-typed';

/*------------- CUSTOM ------------------------------------------------------*/
import { SaveEmpresaAsync, RequestEmpresasAsync, UpdateEmpresaAsync } from 'slices/empresaSlice';

import api from 'Components/_helpers/api';
import axios from 'axios';

const model = SchemaModel({
  nit: StringType()
    .minLength(14, 'Debe contener 14 caracteres')
    .maxLength(14, 'Debe contener 14 caracteres')
    .pattern(/^([0-9]{14}|[0-9]{9})$/, 'Ingresar un NIT Válido.')
    .isRequired('Campo requerido'),
  nrc: StringType()
    .pattern(/^[0-9]{1,8}$/, 'Ingresar un NRC Válido.')
    .isRequired('Campo requerido'),
  nombre: StringType()
    .minLength(1, 'Debe contener al menos un caracter.')
    .maxLength(250, 'Solo se permite hasta 250 caracteres')
    .isRequired('Campo requerido'),
  nombreComercial: StringType(),
  telefono: StringType()
    .minLength(8, 'Debe contener al menos 8 caracteres')
    .maxLength(30, 'Debe contener hasta 30 caracteres')
    .isRequired('Campo requerido'),
  departamento: StringType().isRequired('Campo requerido'),
  municipio: StringType().isRequired('Campo requerido'),
  complemento: StringType()
    .minLength(1, 'Debe contener al menos un caracter.')
    .maxLength(200, 'Solo se permite hasta 200 caracteres')
    .isRequired('Campo requerido'),
  correo: StringType()
    .isEmail()
    .minLength(3, 'Debe contener al menos 3 caracteres')
    .maxLength(100, 'Debe contener hasta 100 caracteres')
    .isRequired('Campo requerido'),
  codActividad: StringType()
    .minLength(5, 'Debe contener al menos 5 caracteres')
    .maxLength(6, 'Debe contener hasta 6 caracteres')
    .pattern(/^[0-9]{1,8}$/, 'Ingresar un Codigo de actividad Válido.')
    .isRequired('Campo requerido'),
  descActividad: StringType().isRequired('Campo requerido'),
  tipoEstablecimiento: StringType().isRequired('Campo requerido'),
  codEstable: StringType().minLength(4, 'Debe contener al menos 4 caracteres').maxLength(4, 'Debe contener hasta 4 caracteres'),
  codEstableMH: StringType().minLength(4, 'Debe contener al menos 4 caracteres').maxLength(4, 'Debe contener hasta 4 caracteres'),
  codPuntoVenta: StringType().minLength(1, 'Debe contener al menos 1 caracteres').maxLength(15, 'Debe contener hasta 15 caracteres'),
  codPuntoVentaMH: StringType().minLength(1, 'Debe contener al menos 1 caracteres').maxLength(15, 'Debe contener hasta 15 caracteres'),
  mhUsr: StringType().isRequired('Campo requerido'),
  mhPass: StringType().isRequired('Campo requerido')
});

const TextField = forwardRef((props, ref) => {
  const { name, label, accepter, ...rest } = props;
  return (
    <Form.Group controlId={`${name}-4`} ref={ref}>
      <Form.ControlLabel>{label} </Form.ControlLabel>
      <Form.Control name={name} accepter={accepter} {...rest} />
    </Form.Group>
  );
});

const defaultForm = {
  nombre: '',
  nombreComercial: '',
  nit: '',
  nrc: '',
  departamento: '',
  municipio: '',
  complemento: '',
  correo: '',
  codActividad: '',
  descActividad: '',
  tipoEstablecimiento: '',
  codEstable: '',
  codEstableMH: '',
  codPuntoVenta: '',
  codPuntoVentaMH: '',
  telefono: '',
  mhUsr: '',
  mhPass: ''
};

export default function EmpresaForm(props) {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [catDefs, setCatDefs] = useState({ loading: true });
  const { data, close } = props;
  const toaster = useToaster();

  const [formValue, setFormValue] = useState({
    id: null,
    nombre: '',
    nombreComercial: '',
    nit: '',
    nrc: '',
    departamento: '',
    municipio: '',
    complemento: '',
    correo: '',
    telefono: '',
    codActividad: '',
    descActividad: '',
    tipoEstablecimiento: '',
    codEstable: '',
    codEstableMH: '',
    codPuntoVenta: '',
    codPuntoVentaMH: '',
    mhUsr: '',
    mhPass: ''
  });

  const handleSubmit = async () => {
    let formStatus = await formRef.current.checkAsync();

    if (formStatus.hasError) {
      return;
    } else {
      if (formValue && formValue.id) {
        setLoading(true);
        dispatch(UpdateEmpresaAsync(formValue))
          .then((res) => {
            toaster.push(
              <Notification type={'success'} header={`DTE Manager`} closable>
                <p>Empresa guardada con exito</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
            dispatch(RequestEmpresasAsync({}));
            close();
          })
          .catch((err) => {
            let message = err.response.data;
            toaster.push(
              <Notification type={'error'} header={`DTE Manager`} closable>
                <p>{message}</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
          });
      } else {
        setLoading(true);
        dispatch(SaveEmpresaAsync(formValue))
          .then((res) => {
            toaster.push(
              <Notification type={'success'} header={`DTE Manager`} closable>
                <p>Empresa creada con exito</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
            dispatch(RequestEmpresasAsync({}));
            close();
          })
          .catch((err) => {
            let message = err.response.data;
            toaster.push(
              <Notification type={'error'} header={`DTE Manager`} closable>
                <p>{message}</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
          });
      }
    }
  };

  const handleChangeForm = (data) => {
    setFormValue(data);
  };

  const getCATDef = () => {
    const catTipoEstablecimiento = api.post(`${api.getUri()}CAT/CAT009`, { code: null });
    const catTipoDocumento = api.post(`${api.getUri()}CAT/CAT022`, { code: null });
    const catTipoContingencia = api.post(`${api.getUri()}CAT/CAT005`, { code: null });

    const catDepartamento = api.post(`${api.getUri()}CAT/CAT012`, { code: null, pagination: { numberPage: 1, sizePage: 20 } });
    const catMunicipio = api.post(`${api.getUri()}CAT/CAT013`, { code: null, pagination: { numberPage: 1, sizePage: 300 } });

    axios.all([catTipoEstablecimiento, catTipoDocumento, catTipoContingencia, catDepartamento, catMunicipio]).then(
      axios.spread((tipoEstablecimiento, tipoDocumento, tipoContingencia, departamento, municipio) => {
        setCatDefs({
          loading: false,
          tipoEstablecimiento: tipoEstablecimiento.data.items,
          tipoDocumento: tipoDocumento.data.items,
          tipoContingencia: tipoContingencia.data.items,
          departamento: departamento.data.items,
          municipio: municipio.data.items,
        });
      })
    );
  };

  const valCodActividad = (val, evt) => {
    model.checkAsync({ codActividad: val }).then((checkResult) => {
      if (!checkResult.codActividad.hasError)
        api.post(`${api.getUri()}CAT/CAT019`, { code: val }).then((res) => {
          const item = res.data.items.pop();
          if (item)
            setFormValue((prevState) => ({
              ...prevState,
              codActividad: item.code,
              descActividad: item.value,
            }));
        });
      else
        setFormValue((prevState) => ({
          ...prevState,
          descActividad: '',
        }));
    });
  };

  useEffect(() => {
    getCATDef();
    if (data) {
      setFormValue((prevState) => ({
        ...prevState,
        id: data.id,
        nombre: data.nombre,
        nombreComercial: data.nombreComercial,
        nit: data.nit,
        nrc: data.nrc,
        departamento: data.departamento,
        municipio: data.municipio,
        complemento: data.complemento,
        correo: data.correo,
        telefono: data.telefono,
        codActividad: data.codActividad,
        descActividad: data.descActividad,
        tipoEstablecimiento: data.tipoEstablecimiento,
        codEstable: data.codEstable,
        codEstableMH: data.codEstableMH,
        codPuntoVenta: data.codPuntoVenta,
        codPuntoVentaMH: data.codPuntoVentaMH,
        mhUsr: data.mhUsr,
        mhPass: data.mhPass
      }));
    } else {
      setFormValue(defaultForm);
    }
  }, [data]);

  return (
    <Grid fluid>
      <Row>
        <Col xs={24}>
          <Form ref={formRef} onChange={handleChangeForm} formValue={formValue} model={model}>
            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    name="nit"
                    checkAsync
                    classPrefix="full_width"
                    placeholder="NIT"
                    label="NIT (Número de identificación tributaria)"
                    autoComplete="off"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField
                    name="nrc"
                    checkAsync
                    classPrefix="full_width"
                    placeholder="NRC"
                    label="NRC (Número de Registro de Contribuyente)"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={14}>
                  <TextField name="nombre" classPrefix="full_width" placeholder="Nombre" label="Nombre" autoComplete="off" />
                </Col>
                <Col xs={24} md={10}>
                  <TextField
                    name="nombreComercial"
                    classPrefix="full_width"
                    placeholder="Nombre Comercial"
                    label="Nombre Comercial"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    name="departamento"
                    classPrefix="full_width"
                    block
                    accepter={SelectPicker}
                    loading={catDefs.loading}
                    data={catDefs.loading ? [] : catDefs.departamento.map((c) => ({ label: c.value, value: c.code }))}
                    placeholder="Departamento"
                    label="Departamento"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField
                    name="municipio"
                    classPrefix="full_width"
                    block
                    accepter={SelectPicker}
                    loading={catDefs.loading}
                    data={catDefs.loading ? [] : catDefs.municipio.map((c) => ({ label: c.value, value: c.code }))}
                    placeholder="Municipio"
                    virtualized
                    label="Municipio"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={24}>
                  <TextField name="complemento" classPrefix="full_width" placeholder="Complemento" label="Complemento" autoComplete="off" />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    name="telefono"
                    classPrefix="full_width"
                    placeholder="Teléfono"
                    label="Teléfono"
                    type="text"
                    autoComplete="off"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField name="correo" classPrefix="full_width" placeholder="Correo" label="Correo" type="email" autoComplete="off" />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row className="show-grid">
                <Col xs={24} md={8}>
                  <TextField
                    name="codActividad"
                    classPrefix="full_width"
                    placeholder="Código"
                    label="Codigo Actividad"
                    type="text"
                    autoComplete="off"
                    onChange={valCodActividad}
                  />
                </Col>
                <Col xs={24} md={16}>
                  <TextField
                    name="descActividad"
                    classPrefix="full_width"
                    placeholder="Descripción"
                    label="Descripcion Actividad"
                    type="text"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row className="show-grid">
                <Col xs={24} md={24}>
                  <TextField
                    name="tipoEstablecimiento"
                    classPrefix="full_width"
                    placeholder="Código"
                    label="Tipo de establecimiento"
                    block
                    accepter={SelectPicker}
                    loading={catDefs.loading}
                    data={catDefs.loading ? [] : catDefs.tipoEstablecimiento.map((c) => ({ label: c.value, value: c.code }))}
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row className="show-grid">
                <Col xs={24} md={8}>
                  <TextField
                    name="mhUsr"
                    classPrefix="full_width"
                    placeholder="Usuario MH"
                    label="Usuario MH"
                    type="password"
                    autoComplete="off"
                  />
                </Col>
                <Col xs={24} md={16}>
                  <TextField
                    name="mhPass"
                    classPrefix="full_width"
                    placeholder="Password MH"
                    label="Password MH"
                    type="password"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    name="codEstable"
                    classPrefix="full_width"
                    placeholder="Descripción"
                    label="Código de establecimiento"
                    type="text"
                    autoComplete="off"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField
                    name="codPuntoVenta"
                    classPrefix="full_width"
                    placeholder="Código"
                    label="Código de punto de venta"
                    type="text"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>

            <Grid fluid>
              <Row>
                <Col>
                  <Button loading={loading} appearance="primary" onClick={handleSubmit}>
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Grid>
          </Form>
        </Col>
      </Row>
    </Grid>
  );
}

EmpresaForm.propTypes = {
  data: PropTypes.object,
};
